import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { ColDef, SideBarDef } from "ag-grid-community";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { Icon } from "@iconify/react";
import SearchInput from "../../components/shared/SearchInput";
import AdvancedAgGridSearch from "../../components/AdvancedAgGridSearch";
import GrantStatusLabel from "../esopOverview/StatusLabel";
import Avatar from "../esopOverview/Avatar";
import { FileDropzone } from "../../components/shared/FileDropzone";
import { Instructions } from "../../components/shared/Instructions";
import { SegmentedTabs } from "../../components/shared/SegmentedTabs";
import { TextArea } from "../../components/shared/InputField";
import {
  ButtonPrimary,
  ButtonPrimary1,
  ExportTableData,
  IconCTAButton,
  TableDataExportProps,
  decodeAuthToken,
  isAdmin,
} from "../../components/utils";
import { useAuthStore, usePermissionStore } from "../../store";
import { formatDisplayDate } from "../../utils/date";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import { useCompanyStore } from "../../store/companyStore";
import { useGetAllMatrices } from "../../queries/allocationMatrix";
import { useValuationDetails } from "../../queries/Valuation";
import { SignOffDecision } from "../../types/GrantApproval";
import { ConversionMethodology } from "../../types/Exercise";
import {
  useDownloadBulkRecommendationTemplate,
  useParseBulkRecommendationUpload,
  useSubmitBulkApproverActions,
} from "../../queries/grantApproval";
import convertToBase64 from "../../utils/convertToBase64";
import { useTableStateManagement } from "../../components/shared/TableHook";
import { TableId } from "../../constants/TableIdConstants";
import CloudSetting from "../../components/shared/CloudIcon";
import { BulkActions, ExportImport } from "../esopOverview/GrantsTable";
import { GrantApprovalActionDialog } from "./GrantApprovalActionDialog";
import { BulkRecommendChangeDialog } from "../esopOverview/BulkRecommendChangeDialog";
import {
  AllocationMode,
  CostOrGain,
  QuantumMode,
  RuleScope,
} from "../../store/useAllocationMatrixStore";
import { ValuationSource } from "../../types/ValuationType";
import {
  calculateAllocationValue,
  getOriginalAllocationRule,
} from "../../utils/allocationMatrixValue";
import {
  GrantMenuRenderer,
  GrantStateBadge,
  ManagerGrantDetailsView,
  ManagerVestingView,
} from "./TeamMembersGrid";

type ManagerGrantRow = {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeHrId: string;
  department: string | null;
  identifier: string;
  planName: string | null;
  dateOfGrant: string;
  grantPrice: number | null;
  grantedOptions: number;
  activeGranted: number;
  vestedOptions: number;
  unvestedOptions: number;
  netVested: number;
  outstanding: number;
  exercised: number;
  stockExercised: number;
  soldOptions: number;
  soldShares: number;
  ratio: number;
  conversionMethodology: ConversionMethodology;
  cashedOut: number;
  lapsed: number;
  forfeited: number;
  allocationMatrixId: string | null;
  matchedRule: Record<string, any> | null;
  state: string;
  isPendingApprover: boolean;
};

type RecommendationUploadPreviewRow = {
  rowNumber: number;
  identifier: string;
  grantId?: string;
  proposedOptions?: number;
  note?: string;
  errors: string[];
};

const managerGrantExportHeaders = [
  { key: "employeeName", value: "Employee Name" },
  { key: "employeeHrId", value: "Employee ID" },
  { key: "department", value: "Department" },
  { key: "identifier", value: "Grant ID" },
  { key: "planName", value: "Plan Name" },
  { key: "dateOfGrant", value: "Grant Date" },
  { key: "grantPrice", value: "Grant Price" },
  { key: "state", value: "Grant Status" },
  { key: "grantedOptions", value: "Options Granted" },
  { key: "activeGranted", value: "Active Granted" },
  { key: "vestedOptions", value: "Vested" },
  { key: "unvestedOptions", value: "Unvested" },
  { key: "netVested", value: "Net Vested" },
  { key: "stockExercised", value: "Stock Exercised" },
  { key: "soldShares", value: "Sold Shares" },
  { key: "soldOptions", value: "Sold Options" },
  { key: "cashedOut", value: "Cashed Out" },
  { key: "exercised", value: "Total Exercised" },
  { key: "lapsed", value: "Lapsed" },
  { key: "forfeited", value: "Forfeited" },
];

export function ManagerGrantsGrid({
  members,
  onViewEmployeeGrants,
}: {
  members: any[];
  onViewEmployeeGrants?: (employeeId: string) => void;
}) {
  const gridRef = useRef<AgGridReact>(null);
  const [search, setSearch] = useState("");
  const [displayedGrantCount, setDisplayedGrantCount] = useState(0);
  const [displayedEmployeeCount, setDisplayedEmployeeCount] = useState(0);
  const [showActionRequiredOnly, setShowActionRequiredOnly] = useState(false);
  const [reviewGrant, setReviewGrant] = useState<ManagerGrantRow | null>(null);
  const [grantDetailsId, setGrantDetailsId] = useState<string | null>(null);
  const [vestingGrant, setVestingGrant] = useState<ManagerGrantRow | null>(
    null
  );
  const [selectedGrants, setSelectedGrants] = useState<ManagerGrantRow[]>([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDecision, setBulkDecision] = useState<"approved" | "rejected">(
    "approved"
  );
  const [bulkNote, setBulkNote] = useState("");
  const [recommendDialogOpen, setRecommendDialogOpen] = useState(false);
  const [recommendations, setRecommendations] = useState<
    Record<string, { optionsGranted: string; note: string }>
  >({});
  const [showRecommendationValidation, setShowRecommendationValidation] =
    useState(false);
  const [recommendationEntryMode, setRecommendationEntryMode] = useState<
    "manual" | "upload"
  >("manual");
  const [recommendationFile, setRecommendationFile] = useState<File>();
  const [recommendationUploadPreview, setRecommendationUploadPreview] =
    useState<RecommendationUploadPreviewRow[]>([]);
  const [recommendationUploadErrors, setRecommendationUploadErrors] = useState<
    string[]
  >([]);
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [exportData, setExportData] = useState<TableDataExportProps>({});
  const [advancedFilterApi, setAdvancedFilterApi] = useState<any>(null);
  const gridApi = useRef<any>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    mutateAsync: submitBulkApproverActions,
    isLoading: isSubmittingApproverActions,
  } = useSubmitBulkApproverActions();
  const isSubmittingBulkAction = isSubmittingApproverActions;
  const isSubmittingRecommendations = isSubmittingApproverActions;
  const { mutateAsync: parseRecommendationUpload, isLoading: isParsingUpload } =
    useParseBulkRecommendationUpload();
  const { mutateAsync: downloadRecommendationTemplate } =
    useDownloadBulkRecommendationTemplate();
  const { accessToken } = useAuthStore();
  const { permission } = usePermissionStore();
  const { companyData } = useCompanyStore();
  const allocationMatrixEnabled = Boolean(
    companyData?.companySettings?.grantLetterSettings
      ?.useAllocationMatrixForGranting
  );
  const { data: matrices } = useGetAllMatrices();
  const { data: valuations, isPlaceholderData: valuationsAreLoading } =
    useValuationDetails(true);
  const currentUserId = accessToken
    ? decodeAuthToken(accessToken).id?.toString()
    : undefined;
  const userIsAdmin = isAdmin();
  const currencySymbol = getCurrencySymbol();

  const getSoldShares = (
    soldOptions: number,
    ratio: number,
    methodology: ConversionMethodology
  ) => {
    const shares = (soldOptions || 0) * (ratio || 1);
    if (methodology === ConversionMethodology.ROUND_UP)
      return Math.ceil(shares);
    if (methodology === ConversionMethodology.ROUND_DOWN)
      return Math.floor(shares);
    return Math.round(shares);
  };

  const allRows = useMemo<ManagerGrantRow[]>(
    () =>
      members.flatMap((member) =>
        (member.grants ?? []).map((grant: any) => ({
          id: grant.id,
          employeeId: member.id,
          employeeName: member.name,
          employeeHrId: member.hrId,
          department: member.department,
          identifier: grant.identifier,
          planName: grant.planName,
          dateOfGrant: grant.dateOfGrant,
          grantPrice: grant.grantPrice,
          grantedOptions: grant.grantedOptions,
          activeGranted:
            (grant.netVested || 0) +
            (grant.unvestedOptions || 0) -
            (grant.lapsed || 0),
          vestedOptions: grant.vestedOptions,
          unvestedOptions: grant.unvestedOptions,
          netVested: grant.netVested,
          outstanding: grant.outstanding,
          exercised: grant.exercised,
          stockExercised: grant.stockExercised,
          soldOptions: grant.soldOptions || 0,
          soldShares: getSoldShares(
            grant.soldOptions || 0,
            grant.ratio || 1,
            grant.conversionMethodology || ConversionMethodology.ROUND_DOWN
          ),
          ratio: grant.ratio || 1,
          conversionMethodology:
            grant.conversionMethodology || ConversionMethodology.ROUND_DOWN,
          cashedOut: grant.cashedOut,
          lapsed: grant.lapsed,
          forfeited: grant.forfeited,
          allocationMatrixId: grant.allocationMatrixId,
          matchedRule: getOriginalAllocationRule(
            matrices,
            grant.allocationMatrixId,
            grant.matchedRule
          ),
          state: grant.state,
          isPendingApprover:
            grant.state === "FOR_APPROVAL" && !!grant.isPendingApprover,
        }))
      ),
    [members, matrices]
  );

  const rowData = useMemo(
    () =>
      showActionRequiredOnly
        ? allRows.filter((grant) => grant.isPendingApprover)
        : allRows,
    [allRows, showActionRequiredOnly]
  );

  const selectedEmployeeCount = useMemo(
    () => new Set(selectedGrants.map((grant) => grant.employeeId)).size,
    [selectedGrants]
  );

  const grantCountForHeader = selectedGrants.length
    ? selectedGrants.length
    : displayedGrantCount;
  const employeeCountForHeader = selectedGrants.length
    ? selectedEmployeeCount
    : displayedEmployeeCount;

  const updateDisplayedCounts = (api = gridApi.current?.api) => {
    if (!api) {
      setDisplayedGrantCount(rowData.length);
      setDisplayedEmployeeCount(
        new Set(rowData.map((grant) => grant.employeeId).filter(Boolean)).size
      );
      return;
    }

    const displayedRows: ManagerGrantRow[] = [];
    api.forEachNodeAfterFilter((node: any) => {
      if (node.data) displayedRows.push(node.data);
    });
    setDisplayedGrantCount(displayedRows.length);
    setDisplayedEmployeeCount(
      new Set(displayedRows.map((grant) => grant.employeeId).filter(Boolean))
        .size
    );
  };

  useEffect(() => {
    updateDisplayedCounts();
  }, [rowData]);
  const pinnedBottomRowData = useMemo(() => {
    const totals = rowData.reduce(
      (result, grant) => ({
        grantedOptions: result.grantedOptions + (grant.grantedOptions || 0),
        activeGranted: result.activeGranted + (grant.activeGranted || 0),
        vestedOptions: result.vestedOptions + (grant.vestedOptions || 0),
        unvestedOptions: result.unvestedOptions + (grant.unvestedOptions || 0),
        netVested: result.netVested + (grant.netVested || 0),
        outstanding: result.outstanding + (grant.outstanding || 0),
        exercised: result.exercised + (grant.exercised || 0),
        stockExercised: result.stockExercised + (grant.stockExercised || 0),
        soldOptions: result.soldOptions + (grant.soldOptions || 0),
        soldShares: result.soldShares + (grant.soldShares || 0),
        cashedOut: result.cashedOut + (grant.cashedOut || 0),
        lapsed: result.lapsed + (grant.lapsed || 0),
        forfeited: result.forfeited + (grant.forfeited || 0),
      }),
      {
        grantedOptions: 0,
        activeGranted: 0,
        vestedOptions: 0,
        unvestedOptions: 0,
        netVested: 0,
        outstanding: 0,
        exercised: 0,
        stockExercised: 0,
        soldOptions: 0,
        soldShares: 0,
        cashedOut: 0,
        lapsed: 0,
        forfeited: 0,
      }
    );
    return [{ ...totals, employeeName: "Total" }];
  }, [rowData]);
  const actionRequiredCount = allRows.filter(
    (grant) => grant.isPendingApprover
  ).length;
  const shouldShowActionRequiredButton =
    permission?.role === "APPROVER" || actionRequiredCount > 0;

  const currentSharePrice = useMemo(() => {
    if (companyData?.companyMetaData.typeOfTheCompany === "PUBLIC") {
      return companyData.companyMetaData?.pps ?? 0;
    }
    if (valuations && !valuationsAreLoading) {
      return (
        valuations.find(
          (item: any) => item.valuationSource === ValuationSource.COMPANY
        )?.pps ??
        valuations[0]?.pps ??
        0
      );
    }
    return 0;
  }, [companyData, valuations, valuationsAreLoading]);

  const matrixNameById = useMemo(
    () =>
      (matrices ?? []).reduce<Record<string, string>>((names, matrix: any) => {
        if (matrix.id) names[matrix.id] = matrix.name;
        return names;
      }, {}),
    [matrices]
  );

  const allocationDetails = useMemo(() => {
    const result: Record<
      string,
      { allocatedValue: number; priorGrantCount: number }
    > = {};
    allRows.forEach((grant) => {
      const rule = grant.matchedRule as any;
      const priorGrants = allRows.filter(
        (other) =>
          other.employeeId === grant.employeeId &&
          other.id !== grant.id &&
          other.state !== "WITHDRAW"
      );
      const allocatedValue = priorGrants.reduce(
        (total, prior) =>
          total +
          calculateAllocationValue({
            options: prior.grantedOptions || 0,
            ratio: prior.ratio,
            fmv: currentSharePrice,
            strikePrice: prior.grantPrice || 0,
            allocationMode: rule?.allocationMode,
            costOrGain: rule?.costOrGain,
          }),
        0
      );
      result[grant.id] = {
        allocatedValue,
        priorGrantCount: priorGrants.length,
      };
    });
    return result;
  }, [allRows, currentSharePrice]);
  const allocationCurrency = getCurrencyType();
  const getRecommendationComparableValue = (grant: ManagerGrantRow) => {
    const rule = grant.matchedRule as any;
    if (rule?.allocationMode !== AllocationMode.ABSOLUTE_VALUE) {
      return grant.grantedOptions || 0;
    }
    return calculateAllocationValue({
      options: grant.grantedOptions || 0,
      ratio: grant.ratio,
      fmv: currentSharePrice,
      strikePrice: grant.grantPrice || 0,
      allocationMode: rule.allocationMode,
      costOrGain: rule.costOrGain,
    });
  };
  const formatRecommendationAllocation = (
    grant: ManagerGrantRow,
    value: number
  ) =>
    (grant.matchedRule as any)?.allocationMode === AllocationMode.ABSOLUTE_VALUE
      ? formatCurrency(value, allocationCurrency)
      : `${value.toLocaleString()} options`;
  const getRecommendationLimitStatus = (grant: ManagerGrantRow) => {
    const rule = grant.matchedRule as any;
    if (!grant.allocationMatrixId || !rule) return null;
    const comparable = getRecommendationComparableValue(grant);
    const compared =
      rule.scope === RuleScope.PER_EMPLOYEE
        ? (allocationDetails[grant.id]?.allocatedValue || 0) + comparable
        : comparable;
    if (rule.quantumMax != null && compared > rule.quantumMax) {
      return "Above limit";
    }
    if (rule.quantumMin != null && compared < rule.quantumMin) {
      return "Below limit";
    }
    return "Within limit";
  };
  const formatRecommendationRuleLimits = (
    grant: ManagerGrantRow,
    rule: any
  ) => {
    if (rule?.quantumMode === QuantumMode.MIN) {
      return `Min: ${formatRecommendationAllocation(grant, rule.quantumMin)}`;
    }
    if (rule?.quantumMode === QuantumMode.MAX) {
      return `Max: ${formatRecommendationAllocation(grant, rule.quantumMax)}`;
    }
    return `${
      rule?.quantumMin != null
        ? formatRecommendationAllocation(grant, rule.quantumMin)
        : "—"
    } – ${
      rule?.quantumMax != null
        ? formatRecommendationAllocation(grant, rule.quantumMax)
        : "—"
    }`;
  };
  const hasIneligibleBulkSelection = selectedGrants.some(
    (grant) => !grant.isPendingApprover
  );
  const hasIncompleteRecommendations = selectedGrants.some((grant) => {
    const recommendation = recommendations[grant.id];
    const proposedOptions = recommendation?.optionsGranted?.trim();
    return (
      !proposedOptions ||
      !Number.isFinite(Number(proposedOptions)) ||
      Number(proposedOptions) < 0
    );
  });
  const hasRecommendationUploadErrors =
    recommendationUploadErrors.length > 0 ||
    recommendationUploadPreview.some((row) => row.errors.length > 0);
  const isRecommendationUploadReady =
    Boolean(recommendationFile) &&
    recommendationUploadPreview.length > 0 &&
    !hasRecommendationUploadErrors;
  const canSubmitRecommendations =
    !isParsingUpload &&
    !isSubmittingRecommendations &&
    !hasIncompleteRecommendations &&
    (recommendationEntryMode === "manual" || isRecommendationUploadReady);

  const {
    filterCondition,
    setFilterCondition,
    isSaving: isStateSaving,
    uploadFilter,
    uploadColumn,
  } = useTableStateManagement(gridApi, TableId.managerGrantDashboardTable);

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          maxWidth: 225,
          width: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );

  const columnDefs = useMemo<ColDef[]>(() => {
    const metricColumn = (
      headerName: string,
      field: keyof ManagerGrantRow,
      width = 125
    ): ColDef => ({
      headerName,
      field,
      width,
      minWidth: 110,
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-right-aligned-header",
      cellRenderer: (params: any) => (
        <span className="text-xs font-medium text-gray-700">
          {params.value != null ? Number(params.value).toLocaleString() : "—"}
        </span>
      ),
      filter: "agNumberColumnFilter",
    });

    return [
      {
        colId: "selection",
        headerName: "",
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        pinned: "left",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        sortable: false,
        filter: false,
        suppressMenu: true,
        resizable: false,
      },
      {
        headerName: "Employee",
        field: "employeeName",
        width: 220,
        minWidth: 190,
        cellRenderer: (params: any) => (
          <div className="flex h-full items-center gap-2 leading-tight">
            {!params.node?.rowPinned && <Avatar name={params.value} />}
            <div className="flex flex-col justify-center">
              <span className="text-xs font-medium text-gray-800">
                {params.value}
              </span>
              {!params.node?.rowPinned && (
                <span className="mt-1 text-[11px] text-gray-400">
                  {params.data.employeeHrId || "—"}
                </span>
              )}
            </div>
          </div>
        ),
      },
      {
        headerName: "Grant Info",
        field: "identifier",
        width: 250,
        minWidth: 220,
        getQuickFilterText: (params) =>
          [
            params.data?.identifier,
            params.data?.dateOfGrant,
            params.data?.grantPrice,
          ]
            .filter((value) => value !== undefined && value !== null)
            .join(" "),
        cellRenderer: (params: any) => (
          <div className="flex h-full flex-col justify-center leading-tight">
            {!params.node?.rowPinned && (
              <>
                <span className="font-mono text-xs font-semibold text-gray-800">
                  {params.data.identifier}
                </span>
                <span className="mt-1 text-[11px] text-gray-400">
                  {params.data.dateOfGrant
                    ? formatDisplayDate(params.data.dateOfGrant)
                    : ""}
                  {params.data.grantPrice != null && (
                    <>
                      <span className="mx-1">·</span>
                      {currencySymbol}
                      {Number(params.data.grantPrice).toLocaleString()}
                    </>
                  )}
                </span>
              </>
            )}
          </div>
        ),
      },
      {
        headerName: "Grant Status",
        field: "state",
        width: 180,
        minWidth: 150,
        cellRenderer: (params: any) => {
          if (params.node?.rowPinned) return null;
          return <GrantStatusLabel state={params.value} />;
        },
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (params: any) =>
            params.value ? params.value.replace(/_/g, " ") : params.value,
        },
      },
      {
        headerName: "Options Granted",
        field: "grantedOptions",
        width: 190,
        minWidth: 150,
        cellClass: "ag-right-aligned-cell",
        headerClass: "ag-right-aligned-header",
        cellRenderer: (params: any) => (
          <div className="relative flex h-full flex-col justify-center text-right leading-tight">
            <span className="text-xs font-medium text-gray-800">
              {params.value != null
                ? Number(params.value).toLocaleString()
                : ""}
            </span>
            {!params.node?.rowPinned && params.data.planName && (
              <span className="absolute right-0 top-1/2 mt-2 text-[11px] text-gray-400">
                {params.data.planName}
              </span>
            )}
          </div>
        ),
        filter: "agNumberColumnFilter",
      },
      metricColumn("Active Granted", "activeGranted", 145),
      {
        headerName: "Vested",
        field: "vestedOptions",
        width: 125,
        minWidth: 110,
        cellClass: "ag-right-aligned-cell",
        headerClass: "ag-right-aligned-header",
        cellRenderer: (params: any) => (
          <span className="text-xs font-medium text-gray-700">
            {params.value != null ? Number(params.value).toLocaleString() : "—"}
          </span>
        ),
        filter: "agNumberColumnFilter",
      },
      metricColumn("Unvested", "unvestedOptions"),
      metricColumn("Net Vested", "netVested", 135),
      metricColumn("Stock Exercised", "stockExercised", 150),
      {
        headerName: "SOLD SHARES",
        headerTooltip:
          "Options will not be displayed when the conversion ratio is 1:1.",
        field: "soldShares",
        width: 155,
        minWidth: 150,
        cellClass: "ag-right-aligned-cell",
        headerClass: "ag-right-aligned-header",
        cellRenderer: (params: any) => (
          <div className="relative flex h-full w-full flex-col items-end justify-center text-right leading-tight">
            <span className="text-xs font-medium text-gray-700">
              {params.value != null
                ? Number(params.value).toLocaleString()
                : "—"}
            </span>
            {!params.node?.rowPinned && params.data.ratio !== 1 && (
              <span className="absolute right-0 top-1/2 mt-2 text-[11px] text-gray-400">
                {Number(params.data.soldOptions || 0).toLocaleString()} options
              </span>
            )}
          </div>
        ),
        filter: "agMultiColumnFilter",
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Sold Shares",
              display: "subMenu",
              filter: "agNumberColumnFilter",
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) => params.data?.soldShares,
              },
            },
            {
              title: "Sold Options",
              display: "subMenu",
              filter: "agNumberColumnFilter",
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) => params.data?.soldOptions,
              },
            },
          ],
        },
      },
      metricColumn("Cashed Out", "cashedOut"),
      metricColumn("Total Exercised", "exercised", 145),
      metricColumn("Lapsed", "lapsed"),
      metricColumn("Forfeited", "forfeited"),
      {
        headerName: "",
        colId: "action",
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        pinned: "right",
        sortable: false,
        filter: false,
        suppressMenu: true,
        resizable: false,
        cellStyle: { padding: 0 },
        cellRenderer: GrantMenuRenderer,
        cellRendererParams: {
          onViewGrant: (_employeeId: string, grantId: string) =>
            setGrantDetailsId(grantId),
          onViewVestingSchedule: (
            employeeId: string,
            grantId: string,
            grantIdentifier: string
          ) =>
            setVestingGrant({
              id: grantId,
              employeeId,
              identifier: grantIdentifier,
            } as ManagerGrantRow),
          onApprovalAction: (_employeeId: string, grantId: string) =>
            setReviewGrant({
              id: grantId,
              isPendingApprover: true,
            } as ManagerGrantRow),
        },
      },
    ];
  }, []);

  const openToolPanel = (key: "columns" | "filters") => {
    if (!gridApi.current?.api) return;
    const isOpening = key === "columns" ? !isColumnOpen : !isFilterOpen;
    if (isOpening) gridApi.current.api.openToolPanel(key);
    else gridApi.current.api.closeToolPanel();
    setIsColumnOpen(key === "columns" ? isOpening : false);
    setIsFilterOpen(key === "filters" ? isOpening : false);
  };

  const exportGrants = (scope: "all" | "selected" | "filtered") => {
    const api = gridApi.current?.api;
    const grantsToExport =
      scope === "selected"
        ? selectedGrants
        : scope === "filtered" && api
        ? (() => {
            const grants: ManagerGrantRow[] = [];
            api.forEachNodeAfterFilter((node: any) => {
              if (node.data) grants.push(node.data);
            });
            return grants;
          })()
        : allRows;

    setExportData({
      data: grantsToExport.map((grant) => ({
        ...grant,
        dateOfGrant: grant.dateOfGrant
          ? formatDisplayDate(grant.dateOfGrant)
          : "",
      })),
      headers: managerGrantExportHeaders,
      heading: "Manager Grants",
      fileName: "Manager_Grants",
      open: true,
      tableName: "Manager Grants",
    });
  };

  const submitBulkDecision = async (decision: "approved" | "rejected") => {
    if (!selectedGrants.length) return;
    try {
      await submitBulkApproverActions(
        selectedGrants.map((grant) => ({
          grantId: grant.id,
          action:
            decision === "approved"
              ? SignOffDecision.APPROVED
              : SignOffDecision.REJECTED,
          comment: bulkNote.trim() || undefined,
        }))
      );
      toast(
        `${selectedGrants.length} grant${
          selectedGrants.length === 1 ? "" : "s"
        } ${decision}.`,
        { type: "success", autoClose: 2000 }
      );
      setBulkDialogOpen(false);
      setBulkNote("");
      setSelectedGrants([]);
      gridRef.current?.api?.deselectAll();
      await Promise.all([
        queryClient.invalidateQueries("managerTeam"),
        queryClient.invalidateQueries("managerDashboard"),
        queryClient.invalidateQueries(["pendingSignOffs"]),
      ]);
    } catch (error: any) {
      toast(
        error?.response?.data?.errorRaw ||
          error?.message ||
          "Unable to record the selected sign-offs.",
        { type: "error", autoClose: 2500 }
      );
    }
  };

  const updateRecommendation = (
    grantId: string,
    field: "optionsGranted" | "note",
    value: string
  ) => {
    setRecommendations((current) => ({
      ...current,
      [grantId]: {
        optionsGranted: current[grantId]?.optionsGranted ?? "",
        note: current[grantId]?.note ?? "",
        [field]: value,
      },
    }));
  };

  const handleRecommendationUpload = async (file: File) => {
    try {
      setRecommendationFile(file);
      setRecommendationUploadPreview([]);
      setRecommendationUploadErrors([]);
      const parsed = await parseRecommendationUpload({
        file: (await convertToBase64(file)) as string,
        grants: selectedGrants.map((grant) => ({
          id: grant.id,
          identifier: grant.identifier,
        })),
      });
      setRecommendationUploadPreview(parsed.rows);
      setRecommendationUploadErrors(parsed.errors || []);
      setRecommendations(
        parsed.rows.reduce<
          Record<string, { optionsGranted: string; note: string }>
        >((current, row) => {
          if (
            !row.grantId ||
            row.errors.length > 0 ||
            row.proposedOptions == null
          ) {
            return current;
          }
          return {
            ...current,
            [row.grantId]: {
              optionsGranted: String(row.proposedOptions),
              note: row.note ?? "",
            },
          };
        }, {})
      );
      const hasErrors =
        parsed.errors.length > 0 ||
        parsed.rows.some((row) => row.errors.length > 0);
      setShowRecommendationValidation(hasErrors);
      if (hasErrors) {
        toast("File loaded with validation errors. Review the preview.", {
          type: "warning",
          autoClose: 2500,
        });
      }
    } catch (error: any) {
      setRecommendationFile(undefined);
      setRecommendationUploadPreview([]);
      setRecommendationUploadErrors([]);
      toast(error?.message || "Unable to parse the uploaded file.", {
        type: "error",
        autoClose: 2500,
      });
    }
  };

  const handleDownloadRecommendationTemplate = async () => {
    try {
      const file = await downloadRecommendationTemplate(
        selectedGrants.map((grant) => ({
          identifier: grant.identifier,
          employeeName: grant.employeeName,
          grantedOptions: grant.grantedOptions,
          allocated: grant.allocationMatrixId
            ? formatRecommendationAllocation(
                grant,
                allocationDetails[grant.id]?.allocatedValue || 0
              )
            : "",
          matrixRule: grant.matchedRule
            ? `${
                matrixNameById[grant.allocationMatrixId || ""] ||
                "Allocation rule"
              } · ${
                (grant.matchedRule as any).scope === RuleScope.PER_EMPLOYEE
                  ? "Per employee"
                  : "Per grant"
              }: ${formatRecommendationRuleLimits(grant, grant.matchedRule)}`
            : "",
          limitCheck: getRecommendationLimitStatus(grant) || "",
        }))
      );
      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = "bulk-recommend-change-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast(error?.message || "Unable to download the template.", {
        type: "error",
        autoClose: 2500,
      });
    }
  };

  const submitBulkRecommendations = async () => {
    if (!canSubmitRecommendations) {
      setShowRecommendationValidation(true);
      return;
    }
    try {
      await submitBulkApproverActions(
        selectedGrants.map((grant) => {
          const recommendation = recommendations[grant.id];
          const optionsGranted = recommendation?.optionsGranted?.trim();
          return {
            grantId: grant.id,
            action: "RECOMMEND_CHANGE" as const,
            comment: recommendation?.note?.trim() || undefined,
            proposedChanges: optionsGranted
              ? { optionsGranted: Number(optionsGranted) }
              : undefined,
          };
        })
      );
      toast(
        `Change recommendation${
          selectedGrants.length === 1 ? "" : "s"
        } submitted.`,
        { type: "success", autoClose: 2000 }
      );
      setRecommendDialogOpen(false);
      setRecommendations({});
      setShowRecommendationValidation(false);
      setSelectedGrants([]);
      gridRef.current?.api?.deselectAll();
    } catch (error: any) {
      toast(error?.message || "Unable to submit recommendations.", {
        type: "error",
        autoClose: 2500,
      });
    }
  };

  return (
    <>
      <div className="manager-table-card flex w-full flex-col gap-4">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-slate-900">
              Grant Details
            </h2>
            <p className="mt-0.5 text-sm text-slate-400">
              {grantCountForHeader} Grant
              {grantCountForHeader === 1 ? "" : "s"}, {employeeCountForHeader}{" "}
              Employee
              {employeeCountForHeader === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <BulkActions
              title={
                selectedGrants.length
                  ? `Bulk Actions (${selectedGrants.length})`
                  : "Bulk Actions"
              }
              actions={[
                {
                  name: "Approve selected",
                  disabled:
                    selectedGrants.length === 0 || hasIneligibleBulkSelection,
                  tooltipText: hasIneligibleBulkSelection
                    ? "Bulk approval is available only when every selected grant requires your sign-off"
                    : "Select one or more grants first",
                },
                {
                  name: "Reject selected",
                  disabled:
                    selectedGrants.length === 0 || hasIneligibleBulkSelection,
                  tooltipText: hasIneligibleBulkSelection
                    ? "Bulk rejection is available only when every selected grant requires your sign-off"
                    : "Select one or more grants first",
                },
                {
                  name: "Recommend change",
                  disabled:
                    selectedGrants.length === 0 || hasIneligibleBulkSelection,
                  tooltipText: hasIneligibleBulkSelection
                    ? "Recommendations are available only when every selected grant requires your sign-off"
                    : "Select one or more grants first",
                },
              ]}
              onAction={(action) => {
                if (action.name === "Recommend change") {
                  setRecommendations(
                    selectedGrants.reduce<
                      Record<string, { optionsGranted: string; note: string }>
                    >(
                      (current, grant) => ({
                        ...current,
                        [grant.id]: { optionsGranted: "", note: "" },
                      }),
                      {}
                    )
                  );
                  setShowRecommendationValidation(false);
                  setRecommendationEntryMode("manual");
                  setRecommendationFile(undefined);
                  setRecommendationUploadPreview([]);
                  setRecommendationUploadErrors([]);
                  setRecommendDialogOpen(true);
                  return;
                }
                setBulkDecision(
                  action.name === "Reject selected" ? "rejected" : "approved"
                );
                setBulkDialogOpen(true);
              }}
            />
            <ExportImport
              actions={[
                { name: "Export All To Excel" },
                {
                  name: "Export Selected",
                  disabled: selectedGrants.length === 0,
                },
                {
                  name: "Export Filtered",
                  disabled: !gridApi.current?.api?.isAnyFilterPresent(),
                },
              ]}
              onAction={(action) => {
                if (action.name === "Export All To Excel") exportGrants("all");
                if (action.name === "Export Selected") exportGrants("selected");
                if (action.name === "Export Filtered") exportGrants("filtered");
              }}
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <SearchInput
            className="!w-[300px]"
            value={search}
            onChange={(event) => {
              const value = event.target.value;
              setSearch(value);
              gridRef.current?.api?.setQuickFilter(value);
            }}
            placeholder="Search"
          />
          <div className="flex items-center gap-2">
            {shouldShowActionRequiredButton && (
              <button
                type="button"
                aria-pressed={showActionRequiredOnly}
                onClick={() => setShowActionRequiredOnly((value) => !value)}
                className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                  showActionRequiredOnly
                    ? "border-orange-200 bg-orange-50 text-orange-700"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                <Icon icon="ri:shield-check-line" width={15} />
                My action required ({actionRequiredCount})
              </button>
            )}
            <CloudSetting
              tableId={TableId.managerGrantDashboardTable}
              isLoading={isStateSaving}
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
              gridApi={gridApi}
            />
            <IconCTAButton
              value="Columns"
              onClick={() => openToolPanel("columns")}
              iconName="fluent:column-triple-edit-20-regular"
              className={`manager-table-tool-cta px-4 font-medium items-center flex flex-row ${
                isColumnOpen ? "manager-table-tool-cta-active" : ""
              }`}
              selected={isColumnOpen}
            />
            <IconCTAButton
              value="Filters"
              onClick={() => openToolPanel("filters")}
              iconName="material-symbols:filter-alt"
              className={`manager-table-tool-cta px-4 font-medium items-center flex flex-row ${
                isFilterOpen ? "manager-table-tool-cta-active" : ""
              }`}
              selected={isFilterOpen}
            />
            <AdvancedAgGridSearch
              gridApi={advancedFilterApi}
              tableIdentifier="managerGrantDashboardTable"
            />
          </div>
        </div>

        <div className="h-[600px] w-full overflow-x-auto manager-dashboard-grid manager-grants-grid ag-theme-material">
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            pinnedBottomRowData={pinnedBottomRowData}
            columnDefs={columnDefs}
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
              suppressMovable: true,
              filterParams: { buttons: ["reset"] },
              menuTabs: ["filterMenuTab"],
            }}
            rowSelection="multiple"
            rowMultiSelectWithClick
            suppressRowClickSelection
            onSelectionChanged={() =>
              setSelectedGrants(gridRef.current?.api?.getSelectedRows() ?? [])
            }
            sideBar={sideBar}
            onGridReady={(params) => {
              gridApi.current = params;
              setAdvancedFilterApi(params.api);
              updateDisplayedCounts(params.api);
            }}
            onFilterChanged={(event) => {
              uploadFilter(event.api.getFilterModel());
              updateDisplayedCounts(event.api);
            }}
            onColumnVisible={uploadColumn}
            onColumnMoved={uploadColumn}
            onColumnPinned={uploadColumn}
            onColumnResized={uploadColumn}
            pagination
            paginationPageSize={15}
            rowHeight={56}
            headerHeight={40}
            suppressCellFocus
            suppressMenuHide
            overlayNoRowsTemplate='<span style="padding:10px;color:#9ca3af">No grants found</span>'
          />
        </div>
      </div>

      <Dialog open={exportData.open || false} maxWidth="md">
        <ExportTableData
          onDiscard={() => setExportData({ ...exportData, open: false })}
          data={exportData.data || []}
          fileName={exportData.fileName || "Manager_Grants"}
          headers={exportData.headers || []}
          heading={exportData.heading || "Manager Grants"}
          tableName={exportData.tableName || "Manager Grants"}
          isManagerPortal
        />
      </Dialog>

      <Dialog
        open={!!reviewGrant}
        onClose={() => setReviewGrant(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: "12px", padding: "8px" } }}
      >
        <DialogTitle className="flex items-center justify-between">
          <span className="text-base font-semibold">Grant Approval</span>
          <IconButton aria-label="Close" onClick={() => setReviewGrant(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {reviewGrant && (
            <GrantApprovalActionDialog
              grantId={reviewGrant.id}
              currentUserId={currentUserId}
              isCurrentUserApprover={reviewGrant.isPendingApprover}
              onClose={() => setReviewGrant(null)}
              onViewGrantHistory={() => {
                const employeeId = reviewGrant.employeeId;
                setReviewGrant(null);
                onViewEmployeeGrants?.(employeeId);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!grantDetailsId}
        onClose={() => setGrantDetailsId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between">
          <span className="text-base font-semibold">Grant Details</span>
          <IconButton
            aria-label="Close"
            onClick={() => setGrantDetailsId(null)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {grantDetailsId && (
            <ManagerGrantDetailsView grantId={grantDetailsId} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!vestingGrant}
        onClose={() => setVestingGrant(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between">
          <span className="text-base font-semibold">Vesting Schedule</span>
          <IconButton aria-label="Close" onClick={() => setVestingGrant(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {vestingGrant && (
            <ManagerVestingView
              employeeId={vestingGrant.employeeId}
              grantId={vestingGrant.id}
              grantIdentifier={vestingGrant.identifier}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={bulkDialogOpen}
        onClose={() => !isSubmittingBulkAction && setBulkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between">
          <span className="text-base font-semibold">
            Bulk {bulkDecision === "approved" ? "Approval" : "Rejection"}
          </span>
          <IconButton
            aria-label="Close"
            disabled={isSubmittingBulkAction}
            onClick={() => setBulkDialogOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <p className="text-sm text-gray-600">
            {bulkDecision === "approved" ? "Approve" : "Reject"} all{" "}
            {selectedGrants.length} selected grant
            {selectedGrants.length === 1 ? "" : "s"}. Only grants where your
            sign-off is currently required can be selected.
          </p>
          <TextArea
            className="mt-4"
            placeholder="Add an optional note for this decision"
            value={bulkNote}
            onChange={(event) => setBulkNote(event.target.value)}
          />
          <div className="mt-5 flex justify-end gap-3">
            {bulkDecision === "rejected" ? (
              <ButtonPrimary1
                loading={isSubmittingBulkAction}
                onClick={() => submitBulkDecision("rejected")}
              >
                Reject selected
              </ButtonPrimary1>
            ) : (
              <ButtonPrimary
                loading={isSubmittingBulkAction}
                onClick={() => submitBulkDecision("approved")}
              >
                Approve selected
              </ButtonPrimary>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={recommendDialogOpen}
        onClose={() =>
          !isSubmittingRecommendations && setRecommendDialogOpen(false)
        }
        maxWidth={recommendationEntryMode === "upload" ? "lg" : "xl"}
        fullWidth
        PaperProps={{
          style: {
            minHeight: recommendationEntryMode === "upload" ? 620 : undefined,
          },
        }}
      >
        <DialogTitle className="flex items-center justify-between">
          <span className="text-base font-semibold">Recommend Change</span>
          <IconButton
            aria-label="Close"
            disabled={isSubmittingRecommendations}
            onClick={() => setRecommendDialogOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <BulkRecommendChangeDialog
            open={recommendDialogOpen}
            embedded
            managerScoped
            onEntryModeChange={setRecommendationEntryMode}
            grants={
              selectedGrants.map((grant) => ({
                optionHolderId: grant.id,
                grantIdentifier: grant.identifier,
                optionHolderName: grant.employeeName,
                employeeId: grant.employeeId,
                optionsGranted: grant.grantedOptions,
                grantPrice: grant.grantPrice,
                ratio: grant.ratio,
                optionHolderState: grant.state,
                allocationMatrixId: grant.allocationMatrixId,
                allocationMatrixName:
                  (grant.allocationMatrixId
                    ? matrixNameById[grant.allocationMatrixId]
                    : undefined) ?? "",
                matchedRule: grant.matchedRule,
              })) as any
            }
            onClose={() => setRecommendDialogOpen(false)}
            onSubmitted={() => {
              setRecommendDialogOpen(false);
              setSelectedGrants([]);
              gridRef.current?.api?.deselectAll();
              queryClient.invalidateQueries("managerTeam");
              queryClient.invalidateQueries("managerDashboard");
              navigate("/manager-portal/dashboard", { replace: true });
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={false}
        onClose={() =>
          !isSubmittingRecommendations && setRecommendDialogOpen(false)
        }
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between">
          <span className="text-base font-semibold">Recommend Change</span>
          <IconButton
            aria-label="Close"
            disabled={isSubmittingRecommendations}
            onClick={() => setRecommendDialogOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div className="w-full">
            {recommendationEntryMode === "manual" && (
              <p className="mb-4 text-sm text-gray-600">
                Propose a revised number of options for each selected grant.
                Notes are optional.
              </p>
            )}
            <SegmentedTabs
              className="mb-7"
              activeTab={recommendationEntryMode}
              onChange={setRecommendationEntryMode}
              tabs={[
                { id: "manual", label: "Manual Input" },
                { id: "upload", label: "File Upload" },
              ]}
            />
            {recommendationEntryMode === "upload" ? (
              <div className="mx-auto w-full max-w-[760px] space-y-7 pb-5">
                <div className="text-center space-y-2">
                  <h3 className="text-xl font-medium text-gray-900">
                    Upload File
                  </h3>
                  <p className="text-gray-500">
                    Download the prefilled template, complete it, then upload it
                    here.
                  </p>
                  <button
                    type="button"
                    onClick={handleDownloadRecommendationTemplate}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    Download prefilled template
                  </button>
                </div>
                <FileDropzone
                  onFileSelect={handleRecommendationUpload}
                  onClear={() => {
                    setRecommendationFile(undefined);
                    setRecommendations({});
                    setRecommendationUploadPreview([]);
                    setRecommendationUploadErrors([]);
                    setShowRecommendationValidation(false);
                  }}
                  selectedFile={recommendationFile}
                  disabled={isParsingUpload || isSubmittingRecommendations}
                  acceptedFileExtensions={[".xlsx"]}
                  instructionText="Excel (.xlsx) with prefilled grant and allocation details, Proposed Change and optional Note columns"
                />
                {isParsingUpload && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Icon icon="eos-icons:loading" className="animate-spin" />
                    <span>Validating file...</span>
                  </div>
                )}
                {recommendationUploadPreview.length > 0 && (
                  <div className="overflow-x-auto rounded-md border border-gray-200">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-3 py-2">Row</th>
                          <th className="px-3 py-2">Grant identifier</th>
                          <th className="px-3 py-2">Proposed change</th>
                          <th className="px-3 py-2">Validation</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {recommendationUploadPreview.map((row) => (
                          <tr key={row.rowNumber}>
                            <td className="px-3 py-2">{row.rowNumber}</td>
                            <td className="px-3 py-2">
                              {row.identifier || "—"}
                            </td>
                            <td className="px-3 py-2">
                              {row.proposedOptions ?? "—"}
                            </td>
                            <td
                              className={`px-3 py-2 ${
                                row.errors.length
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              {row.errors.length
                                ? row.errors.join(", ")
                                : "Valid"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {recommendationUploadErrors.length > 0 && (
                      <div className="border-t border-gray-200 px-3 py-2 text-sm text-red-600">
                        {recommendationUploadErrors.join("; ")}
                      </div>
                    )}
                  </div>
                )}
                <Instructions
                  data={[
                    "Download the prefilled template first",
                    "Proposed Change is required for every selected grant",
                    "Note is optional",
                    "The uploaded rows must match the selected grants exactly",
                  ]}
                />
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="w-full min-w-[1180px] text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-semibold">
                        Grant Identifier
                      </th>
                      <th className="px-4 py-3 font-semibold">Employee Name</th>
                      <th className="px-4 py-3 font-semibold">
                        No. of Options
                      </th>
                      {allocationMatrixEnabled && (
                        <>
                          <th className="px-4 py-3 font-semibold">Allocated</th>
                          <th className="px-4 py-3 font-semibold">
                            Matrix Rule
                          </th>
                          <th className="px-4 py-3 font-semibold">
                            Limit Check
                          </th>
                        </>
                      )}
                      <th className="px-4 py-3 font-semibold">
                        Proposed Change
                      </th>
                      <th className="px-4 py-3 font-semibold">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedGrants.map((grant) => {
                      const rule = grant.matchedRule as any;
                      const limitStatus = getRecommendationLimitStatus(grant);
                      return (
                        <tr key={grant.id}>
                          <td className="px-4 py-3 font-mono font-medium text-gray-800">
                            {grant.identifier}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {grant.employeeName}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {grant.grantedOptions.toLocaleString()}
                          </td>
                          {allocationMatrixEnabled && (
                            <>
                              <td className="px-4 py-3 text-gray-700">
                                {grant.allocationMatrixId
                                  ? formatRecommendationAllocation(
                                      grant,
                                      allocationDetails[grant.id]
                                        ?.allocatedValue || 0
                                    )
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 text-xs leading-5 text-gray-700">
                                {rule ? (
                                  <>
                                    <div className="font-medium text-gray-800">
                                      {matrixNameById[
                                        grant.allocationMatrixId || ""
                                      ] || "Allocation rule"}
                                    </div>
                                    <div>
                                      {rule.scope === RuleScope.PER_EMPLOYEE
                                        ? "Per employee"
                                        : "Per grant"}
                                    </div>
                                    <div className="text-gray-400">
                                      {formatRecommendationRuleLimits(
                                        grant,
                                        rule
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-4 py-3 text-xs font-medium">
                                {limitStatus ? (
                                  <span
                                    className={
                                      limitStatus === "Within limit"
                                        ? "text-green-600"
                                        : "text-orange-600"
                                    }
                                  >
                                    {limitStatus}
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                            </>
                          )}
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              min="0"
                              value={
                                recommendations[grant.id]?.optionsGranted ?? ""
                              }
                              onChange={(event) =>
                                updateRecommendation(
                                  grant.id,
                                  "optionsGranted",
                                  event.target.value
                                )
                              }
                              placeholder="Revised options"
                              className={`w-full rounded border px-2.5 py-2 text-sm outline-none focus:border-orange-400 ${
                                showRecommendationValidation &&
                                (!recommendations[
                                  grant.id
                                ]?.optionsGranted?.trim() ||
                                  !Number.isFinite(
                                    Number(
                                      recommendations[grant.id]?.optionsGranted
                                    )
                                  ) ||
                                  Number(
                                    recommendations[grant.id]?.optionsGranted
                                  ) < 0)
                                  ? "border-red-400"
                                  : "border-gray-200"
                              }`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              value={recommendations[grant.id]?.note ?? ""}
                              onChange={(event) =>
                                updateRecommendation(
                                  grant.id,
                                  "note",
                                  event.target.value
                                )
                              }
                              placeholder="Optional note"
                              className="w-full rounded border border-gray-200 px-2.5 py-2 text-sm outline-none focus:border-orange-400"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {showRecommendationValidation &&
              (hasIncompleteRecommendations ||
                (recommendationEntryMode === "upload" &&
                  (!isRecommendationUploadReady ||
                    hasRecommendationUploadErrors))) && (
                <p className="mt-3 text-sm text-red-600">
                  Fix all validation errors before submitting the
                  recommendation.
                </p>
              )}
          </div>
        </DialogContent>
        <DialogActions className="min-h-[88px] border-t border-gray-200 px-8 py-5">
          <ButtonPrimary
            disabled={!canSubmitRecommendations}
            loading={isSubmittingRecommendations || isParsingUpload}
            onClick={submitBulkRecommendations}
          >
            Recommend change for selected
          </ButtonPrimary>
        </DialogActions>
      </Dialog>
    </>
  );
}
